// M5 — universal AI helper component, reusable across surfaces.
//
// Usage:
//   <AiHelperBubble surface="details" context={{ comune: c }} />
//
// Renders a floating "?" button (bottom-right) that expands into a chat
// panel. Each surface defines its own `surface` string + context shape
// — the backend has matching system prompts at /api/ai/ask.
//
// v0 scope: single question/answer per session (no chat history), no
// streaming. Switching to streamed SSE responses is a follow-up if it
// matters for perceived latency.

import { useState, type FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { Bot } from 'lucide-react';
import { useUnitTerms } from '@/features/map-core/useUnitTerms';
import { getSuggestedPrompts, type AiSurface } from './suggested-prompts';
import { LinkedAnswerText } from './LinkedAnswerText';
import { uiLang } from '@/i18n/lang';

type Surface = AiSurface;

export interface ContextChip {
  /** Short label rendered on the chip. */
  label: string;
  /** Optional onDismiss handler — chip shows × when provided. */
  onDismiss?: () => void;
}

interface Props {
  surface: Surface;
  context: unknown;
  /**
   * Optional visible "what context am I sending" chips. Shown above the
   * input so the user understands the assistant is grounded on specific
   * data (country code, region, comune, persona). If a chip provides
   * `onDismiss`, the user can drop that scope from future calls — useful
   * for "ask general question, not about this specific comune".
   */
  contextChips?: ContextChip[];
  /**
   * Where to dock the floating button + panel. Default 'br' (bottom-right).
   * Use 'br-map' on the country map routes (/<cc>/map): bottom-right on mobile (no ZoomControl),
   * raised to bottom-24 on desktop to clear Leaflet's ZoomControl (~64px).
   * 'bl' kept for legacy but currently unused.
   */
  placement?: 'br' | 'bl' | 'br-map';
}

const PLACEMENT_CLASS: Record<NonNullable<Props['placement']>, string> = {
  br: 'bottom-4 right-4',
  bl: 'bottom-4 left-4',
  // Map-specific: bottom-right but raised above Leaflet ZoomControl on desktop.
  // ZoomControl is ~64px tall; sm:bottom-24 (96px) clears it with a small gap.
  // On mobile ZoomControl is disabled, so bottom-4 is fine.
  'br-map': 'bottom-4 right-4 sm:bottom-24',
};

interface AskResponse {
  answer: string;
  stub?: boolean;
  error?: string;
}

const API_BASE = import.meta.env.VITE_API_URL ?? '';

export function AiHelperBubble({
  surface,
  context,
  contextChips,
  placement = 'br',
}: Props) {
  const { t, i18n } = useTranslation();
  const terms = useUnitTerms();
  const lang = uiLang(i18n.language);
  const dock = PLACEMENT_CLASS[placement];
  const [open, setOpen] = useState(false);
  const [question, setQuestion] = useState('');
  const [answer, setAnswer] = useState<string | null>(null);
  const [stub, setStub] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const suggestions = getSuggestedPrompts(surface);

  const submit = async (q: string) => {
    const trimmed = q.trim();
    if (trimmed.length < 2 || loading) return;
    setLoading(true);
    setAnswer(null);
    setError(null);
    setStub(false);
    try {
      const res = await fetch(`${API_BASE}/api/ai/ask`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ surface, context, question: trimmed, lang }),
      });
      const data = (await res.json().catch(() => ({}))) as AskResponse;
      if (!res.ok) {
        setError(data.error ?? `HTTP ${res.status}`);
      } else if (data.answer) {
        setAnswer(data.answer);
        setStub(!!data.stub);
      } else {
        setError(t('ai.emptyAnswer', { defaultValue: 'Empty answer — try rephrasing.' }));
      }
    } catch {
      setError(t('ai.networkError', { defaultValue: 'Network error.' }));
    }
    setLoading(false);
  };

  const ask = async (e: FormEvent) => {
    e.preventDefault();
    await submit(question);
  };

  const handleSuggested = (q: string) => {
    // Skip if a request is already inflight; otherwise reflect the suggested
    // text in the input so the user can refine it before re-asking.
    if (loading) return;
    setQuestion(q);
    void submit(q);
  };

  if (!open) {
    return (
      <button
        type="button"
        onClick={() => setOpen(true)}
        // Distinguished from the Feedback button visually + semantically:
        //  - Terra-tinted ring + Bot icon (explicit AI signal — sparkles ✨
        //    proved too ambiguous; users didn't read it as an assistant)
        //  - Feedback uses 💬 + sits bottom-left, so icon + corner diverge.
        className={`fixed ${dock} z-[1100] flex h-12 w-12 items-center justify-center rounded-full border-2 border-terra-600 bg-paper text-terra-700 shadow-lg transition-colors hover:bg-terra-50 hover:text-terra-800 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-terra-600`}
        title={t('ai.openBubble', { defaultValue: 'Ask the relocation helper' })}
        aria-label={t('ai.openBubble', { defaultValue: 'Ask the relocation helper' })}
        data-testid="ai-helper-open"
      >
        <Bot className="h-6 w-6" aria-hidden="true" />
      </button>
    );
  }

  return (
    <div
      role="dialog"
      aria-modal="false"
      aria-labelledby="ai-helper-title"
      className={`fixed ${dock} z-[1100] flex w-[min(420px,calc(100vw-2rem))] flex-col rounded-lg border border-ink-200 bg-paper shadow-2xl`}
      data-testid="ai-helper-panel"
    >
      <div className="flex items-center justify-between border-b border-ink-100 px-4 py-2">
        <h2 id="ai-helper-title" className="font-display text-sm italic text-ink-900">
          {t('ai.title', { defaultValue: 'Lodestar helper' })}
        </h2>
        <button
          type="button"
          onClick={() => setOpen(false)}
          aria-label={t('ai.close', { defaultValue: 'Close' })}
          className="text-ink-400 hover:text-ink-900"
        >
          ✕
        </button>
      </div>
      <div className="max-h-[40vh] overflow-y-auto px-4 py-3 text-sm text-ink-700">
        {!answer && !loading && !error && (
          <>
            <p className="font-mono text-[11px] italic text-ink-400">
              {t(`ai.intro.${surface}`, {
                ...terms,
                defaultValue:
                  'Ask anything about what you see here. Grounded on the data we already loaded — no fabricated numbers.',
              })}
            </p>
            {suggestions.length > 0 && (
              <div className="mt-3" data-testid="ai-suggested-prompts">
                <p className="font-mono text-[10px] uppercase tracking-wider text-ink-500">
                  {t('ai.suggestedLabel', { defaultValue: 'Try asking' })}
                </p>
                <ul className="mt-2 flex flex-wrap gap-1.5">
                  {suggestions.map((p) => {
                    const label = lang === 'ru' ? p.label_ru : p.label_en;
                    return (
                      <li key={p.id}>
                        <button
                          type="button"
                          onClick={() => handleSuggested(label)}
                          disabled={loading}
                          className="rounded-full border border-ink-100 bg-paper-2 px-2.5 py-1 text-left text-[11px] text-ink-700 hover:border-terra-600 hover:bg-paper-3 disabled:opacity-50"
                          data-testid={`ai-suggested-${p.id}`}
                        >
                          {label}
                        </button>
                      </li>
                    );
                  })}
                </ul>
              </div>
            )}
          </>
        )}
        {loading && (
          <p className="font-mono text-[11px] italic text-ink-400">
            {t('ai.thinking', { defaultValue: 'Thinking…' })}
          </p>
        )}
        {error && (
          <p className="rounded border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-900">
            {error}
          </p>
        )}
        {answer && (
          <>
            {stub && (
              <p className="mb-2 rounded bg-amber-50 px-2 py-1 font-mono text-[10px] text-amber-800">
                {t('ai.stubBadge', {
                  defaultValue: 'Stub response — Anthropic API key not set on this deployment.',
                })}
              </p>
            )}
            <LinkedAnswerText text={answer} surface={surface} />
            {/* Ask-again affordance after an answer — users with follow-ups
                don't want to re-discover the input. */}
            <button
              type="button"
              onClick={() => {
                setAnswer(null);
                setQuestion('');
              }}
              className="mt-3 font-mono text-[10px] uppercase tracking-wider text-terra-600 hover:text-terra-700"
            >
              {t('ai.askAnother', { defaultValue: '← Ask another' })}
            </button>
          </>
        )}
      </div>
      {/* Context chips — visible scope indicator. Shows the user WHICH
          country / region / comune the assistant is grounded on, so a
          response that mentions "this place" doesn't feel ambiguous.
          Each chip can be dismissed if the caller wired onDismiss — useful
          for "drop the comune lock, ask general regional question". */}
      {contextChips && contextChips.length > 0 && (
        <div
          className="flex flex-wrap items-center gap-1.5 border-t border-ink-100 px-4 pt-2"
          data-testid="ai-context-chips"
        >
          <span className="font-mono text-[10px] uppercase tracking-wider text-ink-500">
            {t('ai.contextLabel', { defaultValue: 'Context' })}
          </span>
          {contextChips.map((chip, i) => (
            <span
              key={`${chip.label}-${i}`}
              className="inline-flex items-center gap-1 rounded-full bg-paper-2 px-2 py-0.5 font-mono text-[11px] text-ink-700"
            >
              {chip.label}
              {chip.onDismiss && (
                <button
                  type="button"
                  onClick={chip.onDismiss}
                  className="text-ink-400 hover:text-ink-900"
                  aria-label={t('ai.dropContext', { defaultValue: 'Drop this scope' })}
                  title={t('ai.dropContext', { defaultValue: 'Drop this scope' })}
                >
                  ×
                </button>
              )}
            </span>
          ))}
        </div>
      )}
      <form onSubmit={ask} className="flex gap-2 border-t border-ink-100 px-4 py-3">
        <input
          type="text"
          value={question}
          onChange={(e) => setQuestion(e.target.value)}
          placeholder={t('ai.placeholder', { defaultValue: 'Ask a question…' })}
          className="min-w-0 flex-1 rounded border border-ink-200 bg-paper px-3 py-1.5 text-xs text-ink-900 focus:outline-none focus:ring-1 focus:ring-terra-600"
          maxLength={1000}
        />
        <button
          type="submit"
          disabled={loading || question.trim().length < 2}
          className="shrink-0 rounded bg-terra-600 px-3 py-1.5 font-mono text-xs font-medium text-white hover:bg-terra-700 disabled:opacity-50"
        >
          {t('ai.submit', { defaultValue: 'Ask' })}
        </button>
      </form>
    </div>
  );
}
